import { useReactiveVar } from '@apollo/client';
import {
  faAddressBook,
  faBuilding,
  faCommentAlt,
  faComments,
  faUser,
} from '@fortawesome/free-regular-svg-icons';
import {
  faCertificate,
  faChartColumn,
  faClipboardList,
  faFile,
  faFileAlt,
  faFileInvoice,
  faFlask,
  faGear,
  faNewspaper,
  faBell,
  faBolt,
  faBuilding as faBuildingSolid,
  faComments as faCommentsSolid,
  faEye,
  faUsers,
  faShirt,
  faShoppingBasket,
  faTachometerAlt,
  faUserFriends,
} from '@fortawesome/free-solid-svg-icons';
import { useMemo } from 'react';

import PageSkeleton from './PageSkeleton';
import { AgencyAvatar, AgencyWrapper } from './styles';

import {
  InvoiceGeneratorIcon,
  JobMonitorIcon,
  NotificationHubIcon,
} from '@/assets/icons';
import NavigationMenu from '@/components/NavigationMenu';
import Skeleton from '@/components/Skeleton';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import { FEATURE_TOGGLE } from '@/constants/featuretoggle';
import Icon from '@/elements/Icon';
import { useAppDispatch } from '@/hooks/store';
import useDashboardRoute from '@/hooks/useDashboardRoute';
import { isStaffAdminUser, isTenantAdminUser } from '@/util/adminRole';
import ROUTES from '@/routes/routes';
import { setRefetchDashboardData } from '@/store/slices/layoutSlice';
import { currentAdminVar, currentAgencyVar } from '@/util/apollo/cache';
import { useFeatureValue } from '@growthbook/growthbook-react';

const Sidebar = () => {
  const currentAgency = useReactiveVar(currentAgencyVar);
  const currentAdmin = useReactiveVar(currentAdminVar);
  const ReleaseNotesAdmin = useFeatureValue(
    FEATURE_TOGGLE.ReleaseNotesAdmin,
    false,
  );

  const dispatch = useAppDispatch();
  // GRAVY-10832 — same rule as SidebarV2: "Dashboard" follows the admin's
  // chosen view. Kept in step so the link does not change meaning when the
  // NavigationImprovements flag flips.
  const { isDashboardV2Admin, dashboardPath } = useDashboardRoute();
  const dashboardClick = () => {
    if (!isDashboardV2Admin && window.location.pathname !== ROUTES.basePath) {
      dispatch(setRefetchDashboardData(true));
    }
  };

  const adminNavigation = useMemo(
    () => [
      {
        id: 'menu-general',
        items: [
          {
            icon: faTachometerAlt,
            a11yLabel: 'Dashboard',
            to: dashboardPath,
            end: true,
            id: 'dashboard-link',
            onClick: dashboardClick,
          },
          {
            icon: faCommentAlt,
            // The unread dot keys off the 'Chat V2' a11yLabel
            // (NavigationMenu.tsx); the visible label stays "Chat".
            a11yLabel: 'Chat V2',
            label: 'Chat',
            to: ROUTES.inbox,
            end: true,
            id: 'inbox-link',
          },
          {
            icon: faCommentAlt,
            a11yLabel: 'Chat',
            to: ROUTES.chat,
            end: true,
            id: 'chat-link',
          },
          {
            icon: faShirt,
            a11yLabel: 'Pending Uniforms',
            label: 'Pending Uniforms',
            to: `/${ROUTES.skillManagement}/${ROUTES.pendingUniforms}`,
            id: 'pending-uniforms-link',
          },
          {
            icon: faCertificate,
            a11yLabel: 'Pending Certificates',
            label: 'Pending Certificates',
            to: `/${ROUTES.skillManagement}/${ROUTES.pendingCertificates}`,
            id: 'pending-certificates-link',
          },
          {
            icon: faCertificate,
            a11yLabel: 'Badges',
            label: 'Badges',
            to: `/${ROUTES.skillManagement}/${ROUTES.badges}`,
            id: 'badges-link',
          },
          {
            icon: faClipboardList,
            a11yLabel: 'Violations',
            label: 'Violations',
            to: ROUTES.tasks,
            id: 'accountability-link',
          },
          {
            icon: faComments,
            a11yLabel: 'Lifecycle',
            label: 'Lifecycle',
            to: ROUTES.lifecycle,
            id: 'lifecycle-link',
          },
          ...(isTenantAdminUser(currentAdmin)
            ? [
                {
                  icon: faClipboardList,
                  a11yLabel: 'AI Assessments',
                  to: ROUTES.aiAssessments,
                  id: 'ai-assessments-link',
                },
                {
                  icon: faFlask,
                  a11yLabel: 'Feature Flags',
                  to: ROUTES.featureFlags,
                  id: 'nav-feature-flags-link',
                },
                {
                  icon: faChartColumn,
                  a11yLabel: 'Analytics Data',
                  label: 'Analytics Data',
                  to: ROUTES.analyticsDashboard,
                  // BARE id — NavigationMenu renders `nav-${item.id}` for both
                  // the DOM id and the roles.json permission lookup. The DOM
                  // contract here is `#nav-analytics-dashboard-link`, and
                  // roles.json already has that bare-prefixed key from D3.
                  id: 'analytics-dashboard-link',
                },
              ]
            : []),
        ],
      },
      {
        id: 'menu-gravywork-team',
        title: 'GravyWork Team',
        items: [
          {
            icon: faBuilding,
            a11yLabel: 'Clients',
            to: ROUTES.clients,
            id: 'clients-link',
          },
          {
            icon: faAddressBook,
            a11yLabel: 'Contacts',
            to: ROUTES.contacts,
            id: 'contacts-link',
          },
          {
            icon: faUserFriends,
            a11yLabel: 'GravyWorkers',
            to: ROUTES.workersV2,
            id: 'workers-link',
          },
        ],
      },
      // Ops automation console — kept in step with SidebarV2's group.
      ...(isStaffAdminUser(currentAdmin)
        ? [
            {
              id: 'menu-automation',
              title: 'Automation',
              items: [
                {
                  icon: faBolt,
                  a11yLabel: 'Overview',
                  to: `/${ROUTES.automationOverview}`,
                  end: true,
                  id: 'automation-overview-link',
                },
                {
                  icon: faBuildingSolid,
                  a11yLabel: 'Client status',
                  to: `/${ROUTES.automationClients}`,
                  id: 'automation-clients-link',
                },
                {
                  icon: faCommentsSolid,
                  a11yLabel: 'Comms and invites',
                  to: `/${ROUTES.automationComms}`,
                  id: 'automation-comms-link',
                },
                {
                  icon: faUsers,
                  a11yLabel: 'Pools and nominations',
                  to: `/${ROUTES.automationPools}`,
                  id: 'automation-pools-link',
                },
                {
                  icon: faEye,
                  a11yLabel: 'Watchlist',
                  to: `/${ROUTES.automationWatchlist}`,
                  id: 'automation-watchlist-link',
                },
              ],
            },
          ]
        : []),
      {
        id: 'menu-admin',
        title: 'Admin',
        items: [
          {
            icon: faShoppingBasket,
            a11yLabel: 'Orders',
            to: ROUTES.orders,
            id: 'orders-link',
          },
          {
            icon: faFileInvoice,
            a11yLabel: 'Invoices',
            to: ROUTES.invoices,
            id: 'invoices-link',
          },
          // Invoice Health is a GravyWork-internal billing tool — tenant admins
          // only. Strict role check, so helpdesk admins are excluded too, not
          // just client admins.
          ...(isTenantAdminUser(currentAdmin)
            ? [
                {
                  icon: faBell,
                  a11yLabel: 'Invoice Health',
                  to: ROUTES.invoiceHealth,
                  id: 'invoice-health-link',
                },
              ]
            : []),
          {
            a11yLabel: 'Invoice Generator',
            to: ROUTES.invoiceGenerator,
            id: 'inv-generator-link',
            CustomIcon: () => (
              <InvoiceGeneratorIcon fontSize="small" htmlColor="inherit" />
            ),
          },
          {
            icon: faFileAlt,
            a11yLabel: 'Payroll Runs',
            to: ROUTES.payrolls,
            id: 'payrolls-link',
          },
          {
            icon: faFile,
            a11yLabel: 'Reports',
            to: ROUTES.reports,
            id: 'reports-link',
          },
          {
            // GRAVY-10878 (OP-01): directly below Reports.
            icon: faGear,
            a11yLabel: 'Operations',
            to: ROUTES.operations,
            id: 'operations-link',
            testId: 'sideNavOperations',
          },
          {
            a11yLabel: 'Shift Monitor',
            to: ROUTES.jobmonitor,
            id: 'jobmonitor-link',
            CustomIcon: () => (
              <JobMonitorIcon fontSize="small" htmlColor="inherit" />
            ),
          },
          ...(ReleaseNotesAdmin
            ? [
                {
                  a11yLabel: 'Notification Hub',
                  to: ROUTES.notificationHub,
                  id: 'notificationhub-link',
                  CustomIcon: () => (
                    <NotificationHubIcon fontSize="small" htmlColor="inherit" />
                  ),
                },
              ]
            : []),
          {
            icon: faNewspaper,
            a11yLabel: 'Posts',
            to: ROUTES.posts,
            id: 'posts-link',
          },
          {
            icon: faFileAlt,
            a11yLabel: 'Documents',
            to: ROUTES.documents,
            id: 'documents-link',
          },
        ],
      },
      {
        id: 'menu-my-information',
        title: 'My Information',
        items: [
          {
            icon: faUserFriends,
            a11yLabel: 'My Team',
            to: ROUTES.myTeam,
            id: 'myteam-link',
          },
          {
            icon: faBuilding,
            a11yLabel: 'My Company',
            to: ROUTES.myCompany,
            id: 'mycompany-link',
          },
          {
            icon: faUser,
            a11yLabel: 'My Profile',
            to: ROUTES.myProfile,
            id: 'profile-link',
          },
        ],
      },
    ],
    [currentAdmin, ReleaseNotesAdmin, dashboardPath],
  );

  return (
    <Stack vertical>
      <AgencyWrapper>
        <Stack gap={16}>
          <div>
            <AgencyAvatar>
              <Icon fixedWidth icon={faBuilding} />
            </AgencyAvatar>
          </div>
          {currentAgency ? (
            <Stack vertical gap={0}>
              <Body weight="semibold">{currentAgency.name}</Body>
              <Small>Washington, DC</Small>
            </Stack>
          ) : (
            <Skeleton.Body lines={2} />
          )}
        </Stack>
      </AgencyWrapper>
      {currentAdmin ? (
        <NavigationMenu groups={adminNavigation} />
      ) : (
        <PageSkeleton />
      )}
    </Stack>
  );
};

export default Sidebar;
